import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Trash2, CheckCircle, CircleAlert, Search, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useEditStockOut, useVoucherDetails, useDeleteStockTransaction } from "@/hooks/useStockTransactions";
import { useItems } from "@/hooks/useItems";
import { useLocation } from "wouter";
import LoadingIndicator from "@/components/LoadingIndicator";
import type { Item } from "@/types/item";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const stockOutSchema = z.object({
  header: z.object({
    voucherNumber: z.string().min(1, "Voucher number is required"),
    voucherDate: z.string().min(1, "Date is required"),
    reference: z.string().optional(),
    remarks: z.string().optional(),
  }),
  details: z
    .array(
      z.object({
        itemId: z.string().min(1, "Please select an item"),
        itemCode: z.string().optional(),
        description: z.string().optional(),
        availableQuantity: z.number().optional(),
        weightedAverage: z.number().optional(),
        quantity: z.number().min(0.01, "Quantity must be greater than 0"),
        costPrice: z.number(),
        amount: z.number().optional(),
      })
    )
    .min(1, "At least one item is required"),
});

type StockOutFormValues = z.infer<typeof stockOutSchema>;

export const StockOutEdit = () => {
  const { format: formatDecimal, step } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const voucherNumber = window.history.state?.voucherNumber ?? null;
  const { data, isLoading: isLoadingVoucher, error } = useVoucherDetails(voucherNumber);
  const { items, isLoading: isLoadingItems } = useItems();
  const editStockOut = useEditStockOut();
  const deleteTransaction = useDeleteStockTransaction();
  const [selectedItemSearch, setSelectedItemSearch] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<StockOutFormValues>({
    resolver: zodResolver(stockOutSchema),
    defaultValues: {
      header: {
        voucherNumber: "",
        voucherDate: format(new Date(), "yyyy-MM-dd"),
        reference: "",
        remarks: "",
      },
      details: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "details",
  });

  const details = watch("details");

  // Load existing data when available
  useEffect(() => {
    if (data?.data && data.data.length > 0 && items?.data) {
      const transactions = data.data;
      const firstTransaction = transactions[0];

      reset({
        header: {
          voucherNumber: firstTransaction.voucherNumber,
          voucherDate: format(new Date(firstTransaction.entryDate), "yyyy-MM-dd"),
          reference: firstTransaction.reference || "",
          remarks: firstTransaction.details || "",
        },
        details: transactions.map((transaction) => {
          // Find the current item to get available quantity
          const currentItem = items.data.find((item: Item) => item.id === transaction.stockCode);
          // Add back the quantity that was removed by this transaction
          const availableQty = (currentItem?.quantity || 0) + transaction.quantity;

          return {
            itemId: transaction.stockCode,
            itemCode: transaction.itemCode,
            description: transaction.itemDescription || "",
            availableQuantity: availableQty,
            weightedAverage: currentItem?.weightedAverage || 0,
            quantity: transaction.quantity,
            costPrice: transaction.baseCurrencyCostOut || 0,
            amount: transaction.totalBaseCurrencyCostOut || 0,
          };
        }),
      });
    }
  }, [data, items, reset]);

  // Calculate total amount
  const totalAmount = details.reduce((sum, item) => {
    const amount = (item.quantity || 0) * (item.costPrice || 0);
    return sum + amount;
  }, 0);

  // Calculate total quantity
  const totalQuantity = details.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Update amount when quantity changes
  const handleCalculation = (index: number) => {
    const item = details[index];
    if (item) {
      const amount = (item.quantity || 0) * (item.costPrice || 0);
      setValue(`details.${index}.amount`, Number(formatDecimal(amount)));
    }
  };

  // Check if quantity exceeds available stock
  const checkInsufficientStock = (index: number): boolean => {
    const item = details[index];
    if (!item) return false;
    return (item.quantity || 0) > (item.availableQuantity || 0);
  };

  const handleAddItem = (item: Item) => {
    // Check if item already exists
    const exists = details.some((d) => d.itemId === item.id);
    if (exists) {
      toast({
        title: "Item Already Added",
        description: "This item is already in the list",
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    // Check if item has stock
    if ((item.quantity || 0) <= 0) {
      toast({
        title: "No Stock Available",
        description: `Item ${item.itemCode} has no available stock`,
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    append({
      itemId: item.id,
      itemCode: item.itemCode,
      description: item.description,
      availableQuantity: item.quantity || 0,
      weightedAverage: item.weightedAverage || 0,
      quantity: 1,
      costPrice: item.weightedAverage || 0,
      amount: item.weightedAverage || 0,
    });

    setSelectedItemSearch("");
  };

  const handleDelete = async () => {
    try {
      await deleteTransaction.mutateAsync(voucherNumber, {
        onSuccess: () => {
          toast({
            title: "Stock Out Deleted",
            description: `Voucher ${voucherNumber} has been deleted successfully`,
            icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
            duration: 5000,
          });
          navigate("/stock-out");
        },
        onError: (error: any) => {
          toast({
            title: "Failed to Delete",
            description: error.response?.data?.message || error.message,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 4000,
          });
        },
      });
    } catch (error) {
      console.error("Error deleting stock out:", error);
    }
  };

  const onSubmit = async (formData: StockOutFormValues) => {
    // Validate stock availability before submission
    const insufficientStock = formData.details.some(
      (item) => (item.quantity || 0) > (item.availableQuantity || 0)
    );

    if (insufficientStock) {
      toast({
        title: "Insufficient Stock",
        description: "One or more items have insufficient stock quantity",
        variant: "destructive",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 4000,
      });
      return;
    }

    try {
      await editStockOut.mutateAsync(
        {
          voucherNumber,
          data: {
            voucherNumber: formData.header.voucherNumber,
            reference: formData.header.reference,
            entryDate: formData.header.voucherDate,
            details: formData.header.remarks,
            items: formData.details.map((item) => ({
              stockCode: item.itemId,
              quantity: item.quantity,
            })),
          },
        },
        {
          onSuccess: (response) => {
            toast({
              title: "Stock Out Updated",
              description: `Voucher ${response.data.voucherNumber} updated successfully`,
              icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
              duration: 5000,
            });
            navigate("/stock-out");
          },
          onError: (error: any) => {
            toast({
              title: "Failed to Update Stock Out",
              description: error.response?.data?.message || error.message,
              variant: "destructive",
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 4000,
            });
          },
        }
      );
    } catch (error) {
      console.error("Error updating stock out:", error);
    }
  };

  const filteredItems = items?.data?.filter((item) => {
    // Filter stock items only
    if (item.stockType !== "stock") return false;

    // If no search term, show all stock items
    if (!selectedItemSearch) return true;

    // Filter by search term
    const searchLower = selectedItemSearch.toLowerCase();
    return (
      item.itemCode.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoadingVoucher || isLoadingItems) return <LoadingIndicator />;

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          Error loading Stock Out details: {error.message}
        </div>
      </div>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
          No Stock Out transactions found for voucher {voucherNumber}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/stock-out")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Edit Stock Out</h1>
        </div>
        <Button
          variant="destructive"
          onClick={() => setShowDeleteDialog(true)}
        >
          Delete Voucher
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Header Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Voucher Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="voucherDate">
                    Date <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    type="date"
                    {...register("header.voucherDate")}
                    className="h-10"
                  />
                  {errors.header?.voucherDate && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.header.voucherDate.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="voucherNumber">
                    Voucher Number <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    {...register("header.voucherNumber")}
                    className="h-10"
                    readOnly
                    disabled
                  />
                </div>

                <div>
                  <Label htmlFor="reference">Reference</Label>
                  <Input
                    {...register("header.reference")}
                    placeholder="e.g., SO-98765"
                    className="h-10"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="remarks">Remarks</Label>
                  <Input
                    {...register("header.remarks")}
                    placeholder="Optional remarks"
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Line Items <span className="text-red-600">*</span>
                </h3>
                <div className="flex gap-2 items-center">
                  <div className="relative w-64">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search items..."
                      value={selectedItemSearch}
                      onChange={(e) => setSelectedItemSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
              </div>

              {/* Item Search Results Dropdown */}
              {selectedItemSearch && filteredItems && filteredItems.length > 0 && (
                <div className="border rounded-md max-h-48 overflow-y-auto">
                  {filteredItems.slice(0, 10).map((item) => (
                    <div
                      key={item.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                      onClick={() => handleAddItem(item)}
                    >
                      <div>
                        <div className="font-medium">{item.itemCode}</div>
                        <div className="text-sm text-gray-600">
                          {item.description}
                        </div>
                      </div>
                      <div className="text-sm text-right">
                        <div className="flex items-center gap-2">
                          <span>Available:</span>
                          <Badge
                            variant={
                              (item.quantity || 0) > 0 ? "default" : "destructive"
                            }
                          >
                            {item.quantity || 0}
                          </Badge>
                        </div>
                        <div>
                          Avg Cost: RM {formatDecimal(item.weightedAverage) || "0.00"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {errors.details?.root && (
                <p className="text-red-600 text-sm">{errors.details.root.message}</p>
              )}

              {/* Line Items Table */}
              {fields.length > 0 && (
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-800">
                    <TableRow>
                      <TableHead>Item Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-28">Available</TableHead>
                      <TableHead className="w-32">Quantity</TableHead>
                      <TableHead className="w-32">Cost Price (RM)</TableHead>
                      <TableHead className="w-32">Amount (RM)</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field, index) => {
                      const hasInsufficientStock = checkInsufficientStock(index);
                      return (
                        <TableRow
                          key={field.id}
                          className={hasInsufficientStock ? "bg-red-50" : ""}
                        >
                          <TableCell className="font-medium">
                            {details[index]?.itemCode}
                          </TableCell>
                          <TableCell>{details[index]?.description}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                hasInsufficientStock ? "destructive" : "default"
                              }
                            >
                              {details[index]?.availableQuantity || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step={step}
                              {...register(`details.${index}.quantity`, {
                                valueAsNumber: true,
                              })}
                              onChange={(e) => {
                                register(`details.${index}.quantity`).onChange(e);
                                setTimeout(() => handleCalculation(index), 0);
                              }}
                              className={`h-8 ${
                                hasInsufficientStock ? "border-red-500" : ""
                              }`}
                            />
                            {hasInsufficientStock && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertTriangle className="h-3 w-3" />
                                <span>Exceeds stock!</span>
                              </div>
                            )}
                            {errors.details?.[index]?.quantity && (
                              <p className="text-red-600 text-xs mt-1">
                                {errors.details[index]?.quantity?.message}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm bg-gray-100 p-2 rounded">
                              {formatDecimal((details[index]?.costPrice || 0))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {formatDecimal((details[index]?.amount || 0))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => remove(index)}
                              className="h-8 w-8 p-0 text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* Totals */}
              {fields.length > 0 && (
                <div className="flex justify-end">
                  <div className="w-96 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Total Quantity:</span>
                      <span className="text-red-600 font-medium">
                        -{formatDecimal(totalQuantity)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total Amount:</span>
                      <span>RM {formatDecimal(totalAmount)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="flex-1 h-14 bg-[#0F172A] text-base font-semibold"
                disabled={editStockOut.isPending}
              >
                {editStockOut.isPending ? "Updating..." : "Update Stock Out"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-14 text-base font-semibold"
                onClick={() => navigate("/stock-out")}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Stock Out Voucher?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete voucher {voucherNumber} and restore all stock quantities.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
